import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Pagination } from '@/components/Pagination';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ShieldCheck, ShieldOff, Loader2 } from 'lucide-react';

const LIFT_TYPES = [
  { value: 'full', label: 'Full access', hint: 'No restrictions — account fully restored' },
  { value: 'restricted', label: 'Restricted', hint: 'Restored, but with the limits you list below' },
  { value: 'probation', label: 'Probation', hint: 'Restored on probation for a set number of days' },
];

const AdminBlockedMembersTab = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState(null);
  const [liftType, setLiftType] = useState('full');
  const [restrictions, setRestrictions] = useState('');
  const [probationDays, setProbationDays] = useState(30);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  });

  const fetchBanned = async (nextPage = page) => {
    try {
      const res = await axios.get(
        `${API}/admin/banned-users?page=${nextPage}&per_page=50`, authHeaders());
      setUsers(res.data.users || []);
      setPage(res.data.page || nextPage);
      setTotalPages(res.data.total_pages || 1);
    } catch (err) {
      toast.error('Failed to load blocked members');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanned(page);
  }, [page]);

  const openLift = (u) => {
    setTarget(u);
    setLiftType('full');
    setRestrictions('');
    setProbationDays(30);
  };

  const submitLift = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${API}/admin/lift-ban?user_id=${target.id}`,
        {
          approval_type: liftType,
          restrictions: restrictions.split(',').map((r) => r.trim()).filter(Boolean),
          probation_days: Number(probationDays) || 30,
        },
        authHeaders()
      );
      toast.success(`${target.full_name || target.username} can log in again`);
      setTarget(null);
      fetchBanned();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not lift the block');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="py-12 text-center text-gray-400">Loading blocked members...</div>;
  }

  return (
    <div className="space-y-4" data-testid="admin-blocked-members-tab">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldOff className="w-5 h-5 text-red-500" />
            Blocked Members
            <Badge variant="secondary" data-testid="blocked-count">{users.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {users.length === 0 ? (
            <p className="text-gray-500 py-6 text-center" data-testid="blocked-empty">
              No members are currently blocked.
            </p>
          ) : (
            <div className="space-y-3">
              {users.map((u) => (
                <div
                  key={u.id}
                  className="flex flex-wrap items-center justify-between gap-3 border border-gray-700 rounded-lg p-3"
                  data-testid={`blocked-user-${u.id}`}
                >
                  <div className="min-w-0">
                    <p className="font-semibold">{u.full_name || u.username}</p>
                    <p className="text-sm text-gray-400 truncate">{u.email}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Blocked {u.banned_at ? new Date(u.banned_at).toLocaleDateString() : 'unknown date'}
                      {u.banned_reason ? ` · ${u.banned_reason}` : ''}
                    </p>
                  </div>
                  <Button
                    onClick={() => openLift(u)}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid={`unblock-btn-${u.id}`}
                  >
                    <ShieldCheck className="w-4 h-4 mr-2" />
                    Unblock
                  </Button>
                </div>
              ))}
              <Pagination
                page={page}
                totalPages={totalPages}
                onChange={setPage}
                testidPrefix="blocked-users-pagination"
                theme="light"
              />
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!target} onOpenChange={(open) => !open && setTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Unblock {target?.full_name || target?.username}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {LIFT_TYPES.map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => setLiftType(t.value)}
                className={`w-full text-left rounded-lg border p-3 transition-colors ${
                  liftType === t.value
                    ? 'border-emerald-400 bg-emerald-500/10'
                    : 'border-gray-600 hover:border-emerald-400/60'
                }`}
                data-testid={`lift-type-${t.value}`}
              >
                <p className="font-semibold">{t.label}</p>
                <p className="text-xs text-gray-500">{t.hint}</p>
              </button>
            ))}

            {liftType === 'restricted' && (
              <div>
                <Label>Restrictions (comma separated)</Label>
                <Input
                  value={restrictions}
                  onChange={(e) => setRestrictions(e.target.value)}
                  placeholder="no_forum_posts, no_direct_messages"
                  data-testid="lift-restrictions-input"
                />
              </div>
            )}

            {liftType === 'probation' && (
              <div>
                <Label>Probation days</Label>
                <Input
                  type="number"
                  min="1"
                  value={probationDays}
                  onChange={(e) => setProbationDays(e.target.value)}
                  data-testid="lift-probation-input"
                />
              </div>
            )}

            <Button
              onClick={submitLift}
              disabled={saving}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-unblock-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
              Restore Access
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBlockedMembersTab;
